import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  useDailyCall,
  useAppendDiscourseTurn,
  useCompleteDiscourseSession,
  useDailyOwnerToken,
  useDiscourseSession,
  useRotateInterviewJoinLink,
  useDiscourseSessionSse,
  dailyCallStateLabel,
  cn,
} from '@yeshuman/sdk'
import type { DailyTranscriptFinal } from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Copy, Link2 } from 'lucide-react'

import { DailyVideoTiles } from './daily-video-tiles'

const JOIN_STORAGE_PREFIX = 'yh.interview.joinToken.'

function joinUrlFromToken(token: string) {
  return `${window.location.origin}/interview/${token}`
}

function roleForTranscript(evt: DailyTranscriptFinal): 'interviewer' | 'candidate' {
  if (evt.local || evt.userId.startsWith('user:')) return 'interviewer'
  return 'candidate'
}

export function InterviewLivePanel({
  sessionId,
  initialJoinToken,
}: {
  sessionId: string
  initialJoinToken?: string | null
}) {
  const sessionQuery = useDiscourseSession(sessionId)
  useDiscourseSessionSse(sessionId, 'interview_coverage')
  const appendTurn = useAppendDiscourseTurn(sessionId)
  const completeSession = useCompleteDiscourseSession(sessionId)
  const ownerToken = useDailyOwnerToken(sessionId)
  const rotateLink = useRotateInterviewJoinLink(sessionId)

  const [joinToken, setJoinToken] = useState<string | null>(() => {
    if (initialJoinToken) return initialJoinToken
    try {
      return sessionStorage.getItem(`${JOIN_STORAGE_PREFIX}${sessionId}`)
    } catch {
      return null
    }
  })
  const [copied, setCopied] = useState(false)
  const [creds, setCreds] = useState<{ room_url: string; token: string } | null>(null)
  const requestedOwnerRef = useRef(false)

  useEffect(() => {
    if (!initialJoinToken) return
    setJoinToken(initialJoinToken)
    try {
      sessionStorage.setItem(`${JOIN_STORAGE_PREFIX}${sessionId}`, initialJoinToken)
    } catch {
      /* ignore quota */
    }
  }, [initialJoinToken, sessionId])

  useEffect(() => {
    if (requestedOwnerRef.current || creds) return
    requestedOwnerRef.current = true
    ownerToken.mutate(undefined, {
      onSuccess: (data) => setCreds({ room_url: data.room_url, token: data.token }),
      onError: () => {
        requestedOwnerRef.current = false
      },
    })
  }, [creds, ownerToken])

  const onFinalTranscript = useCallback(
    (evt: DailyTranscriptFinal) => {
      const text = evt.text.trim()
      if (!text) return
      appendTurn.mutate({
        role: roleForTranscript(evt),
        text,
        turn_id: `daily:${evt.utteranceId}`,
        source: 'provider_event',
      })
    },
    [appendTurn],
  )

  const daily = useDailyCall({
    roomUrl: creds?.room_url,
    token: creds?.token,
    enabled: Boolean(creds) && sessionQuery.data?.status !== 'completed',
    onFinalTranscript,
  })

  const turns = sessionQuery.data?.turns || []
  const status = sessionQuery.data?.status
  const ended = status === 'completed' || daily.state === 'left'

  const guestUrl = useMemo(
    () => (joinToken ? joinUrlFromToken(joinToken) : ''),
    [joinToken],
  )

  const copyLink = async () => {
    if (!guestUrl) return
    try {
      await navigator.clipboard.writeText(guestUrl)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  const onNewLink = () => {
    rotateLink.mutate(undefined, {
      onSuccess: (data) => {
        setJoinToken(data.join_token)
        try {
          sessionStorage.setItem(`${JOIN_STORAGE_PREFIX}${sessionId}`, data.join_token)
        } catch {
          /* ignore */
        }
      },
    })
  }

  const callLabel = dailyCallStateLabel(daily.state)

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={daily.state === 'joined' ? 'successSoft' : 'outline'}>
          {callLabel}
        </Badge>
        {ended ? <Badge variant="secondary">Ended</Badge> : null}
      </div>
      {daily.error || ownerToken.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {daily.error || 'Could not start the video room. Check Daily keys and try again.'}
        </p>
      ) : null}
      <DailyVideoTiles
        participants={daily.participants}
        localLabel="You (interviewer)"
        remoteLabel="Candidate"
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void copyLink()}
          disabled={!guestUrl}
        >
          <Copy className="size-3.5" aria-hidden />
          {copied ? 'Copied' : 'Copy guest link'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghostMuted"
          onClick={onNewLink}
          disabled={rotateLink.isPending}
        >
          <Link2 className="size-3.5" aria-hidden />
          New guest link
        </Button>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          onClick={() => completeSession.mutate()}
          disabled={completeSession.isPending || ended}
        >
          End interview
        </Button>
      </div>
      {guestUrl ? (
        <p className="break-all text-xs text-muted-foreground">{guestUrl}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Guest link is created with the room. Use New guest link if you refreshed.
        </p>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Live transcript</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="max-h-56 space-y-2 overflow-auto"
            aria-live="polite"
            aria-relevant="additions"
          >
            {turns.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Talk to start the transcript. Coaching arrives in the agent thread.
              </p>
            ) : (
              turns.map((t) => (
                <div
                  key={t.id || t.turn_id}
                  className={cn(
                    'rounded-md px-2.5 py-2 text-sm',
                    t.role === 'interviewer'
                      ? 'bg-primary/10'
                      : 'border border-border bg-background',
                  )}
                >
                  <Badge variant="outline" className="mb-1 text-[10px] capitalize">
                    {t.role || 'speaker'}
                  </Badge>
                  <p className="whitespace-pre-wrap">{t.text}</p>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
